import { perPageData } from "@assets/constant/index.constant";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@components/ui/pagination";
import { useSearchParams } from "react-router";

interface PaginationFooterProps {
  action: (update: Partial<{ page: number; per: number }>) => void;
  value: { page: number; per: number };
  total: number;
  showPerPage?: boolean;
}

const PaginationFooter: React.FC<PaginationFooterProps> = ({
  value,
  total,
  showPerPage = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, per } = value;
  const totalPages = Math.max(1, Math.ceil(total / per));
  const currentPage = page;

  // Update local page state when query state changes
  const localPage = parseInt(String(currentPage), 10) || 1;

  // Handle page change
  const handlePageChange = (newPage: number) => {
    searchParams.set("tpage", newPage?.toString());
    setSearchParams(() => searchParams);
    if (newPage >= 1 && newPage <= totalPages) {
    }
  };

  // Handle per-page change
  const handlePerPageChange = (newPer: number) => {
    searchParams.set("per", newPer?.toString());
    searchParams.set("tpage", "1");
    setSearchParams(() => searchParams);
  };

  const getPaginationItems = (
    currentPage: number,
    totalPages: number
  ): (number | string)[] => {
    const pagesToShow = 4;

    if (totalPages <= pagesToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [1, currentPage - 1, currentPage, currentPage + 1, totalPages]
      .filter((page) => page >= 1 && page <= totalPages)
      .filter((value, index, self) => self.indexOf(value) === index); // Ensure no duplicates

    // Ensure that exactly 4 pages are shown
    if (pages.length > pagesToShow) {
      pages.splice(2, pages.length - pagesToShow); // Remove extra pages beyond 4
    }

    // Add ellipses (...) between pages if there are large gaps
    // @ts-ignore
    if (currentPage > 2) pages.splice(1, 0, "..."); // Add "..." after the first page
    // @ts-ignore
    if (currentPage < totalPages - 1) pages.splice(pages.length - 1, 0, "..."); // Add "..." before the last page

    return pages;
  };
  const paginationItems = getPaginationItems(localPage, totalPages);

  const handlePrevious = () => {
    handlePageChange(Math.max(1, localPage - 1));
  };

  const handleNext = () => {
    handlePageChange(Math.min(totalPages, localPage + 1));
  };
  return (
    <div className="flex items-center justify-between max-md:justify-center gap-10 py-1 flex-wrap fs-14-400">
      {showPerPage && (
        <div className="flex items-center flex-wrap max-md:justify-center gap-2 space-x-[4px]">
          Show
          <DropdownMenu>
            <DropdownMenuTrigger className="border cursor-pointer py-1 px-3 rounded-[4px] mx-2">
              {per}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[30px] max-w-[30px] min-w-[50px]  ">
              {perPageData.map((item, index) => (
                <DropdownMenuItem
                  key={index}
                  className={
                    item === per
                      ? "text-[#1DA077] cursor-pointer"
                      : "cursor-pointer"
                  }
                  onClick={() => handlePerPageChange(item)}
                >
                  {item}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          per page Showing {(localPage - 1) * per + 1} to{" "}
          {Math.min(localPage * per, total)} of {total} entries
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-[4px] w-min">
          <Pagination>
            <PaginationContent className="text-gray-600 w-full">
              {/* Previous Button */}

              <PaginationItem>
                <PaginationPrevious
                  className={`select-none border  ${
                    localPage === 1 ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={handlePrevious}
                  // disabled={localPage === 1}
                />
              </PaginationItem>

              <div className="flex items-center gap-2">
                {paginationItems.map((item, index) => (
                  <PaginationItem key={index}>
                    {item === "..." ? (
                      <PaginationEllipsis />
                    ) : (
                      <PaginationLink
                        onClick={() => handlePageChange(Number(item))}
                        className={
                          Number(item) === localPage
                            ? "active bg-[#1DA077]/20 border border-[#1DA077]/40 cursor-pointer"
                            : "cursor-pointer border border-[#E5E7EB] bg-[#F9FAFB]"
                        }
                      >
                        {item}
                      </PaginationLink>
                    )}
                  </PaginationItem>
                ))}
              </div>

              {/* Next Button */}
              <PaginationItem>
                <PaginationNext
                  className={` border select-none ${
                    localPage === totalPages
                      ? "cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  onClick={handleNext}
                  // disabled={localPage === totalPages}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default PaginationFooter;
